import FolderTreeType from 'mini-mail-folder-store/lib/store/schema/FolderTreeType';
import PeopleFilter from 'owa-service/lib/contract/PeopleFilter';
import { FolderValues } from '../utils/configOptions';

export interface handleArrowKeyPressConfig {
    itemIndex: number; //index where the focus is
    itemRefs: React.RefObject<any>[]; //references to all values that are displayed
    length: number; //Total number of indices (helps to aid circularity and prevents access to out of list items)
    configUp?: arrowLocation; //Get the current action from ConfigUp
    folder1ExpandedState?: boolean; //State of the first folder, if present
    folder2ExpandedState?: boolean; //State of the second folder, if present
    configDown?: arrowLocation; //Get the current action from ConfigDown
    rootNode?: boolean; //If the node can expand into subnodes, is true. Else false.
    folderTreeType?: FolderTreeType; //Helps open/close a rootNode
    rootDisplayName?: string; //Name of the folder trying to be opened
    onRootNodeClicked?: () => void; //Action performed if a root node is clicked (expand/ contract)
    onSelect?: (itemKey?: string) => void;
    peopleFilter?: PeopleFilter[]; //Helps select a node (when you press the enter key)
}

export enum arrowLocation {
    PeopleBody,
    PeopleFooter,
    MailBody,
    MailFooter,
    Default,
}

export interface configDirectionResult {
    condition?: boolean;
    conditionMet?: React.RefObject<any>;
    conditionUnMet?: React.RefObject<any>;
    secondCondition?: boolean;
    secondConditionMet?: React.RefObject<any>;
    secondConditionUnMet?: React.RefObject<any>;
    thirdCondition?: boolean;
    thirdConditionMet?: React.RefObject<any>;
    thirdConditionUnMet?: React.RefObject<any>;
}

//Returns the conditions to check for any given situation when a keypress is detected
export function configDirection(
    keyConfig: handleArrowKeyPressConfig,
    configOptions?: FolderValues,
    movementDirectionUp?: boolean
): configDirectionResult {
    const {
        itemIndex,
        itemRefs,
        length,
        configDown = arrowLocation.Default,
        configUp = arrowLocation.Default,
        folder1ExpandedState,
        folder2ExpandedState,
        rootNode,
        rootDisplayName,
        peopleFilter,
    } = keyConfig;

    const {
        peopleContacts,
        mailFavourites,
        mailFavouritesClose,
        mailFolders,
        mailFoldersClose,
    } = configOptions;

    if (movementDirectionUp) {
        switch (configUp) {
            case arrowLocation.PeopleBody: {
                return {
                    condition: itemIndex == 0, //Check if you are in the first itemIndex
                    conditionMet: itemRefs[length - 1], //If it is, go to the last index of the list. Operations end for this key press.
                    secondCondition: itemIndex == peopleContacts.nodeIds.length + 1, // If not in first index, check if you are at the start of the Folders root folder in the people module
                    thirdCondition: folder1ExpandedState == false, //If the second condition is true, and whether the previous folder is closed is checked
                    thirdConditionMet: itemRefs[itemIndex - peopleContacts.nodeIds.length - 1], //If the third condition is true, go to the root node of the Folders folder
                    thirdConditionUnMet: itemRefs[itemIndex - 1], //If the third condition is false, go one index up
                    secondConditionUnMet: itemRefs[itemIndex - 1], //If folder1 is expanded, go up one index (as the folder is open, no need to go to a special location like a root node, just go to the prior index)
                };
            }
            case arrowLocation.MailBody: {
                return {
                    condition: itemIndex == 0, //Check if you are in the first itemIndex
                    conditionMet: itemRefs[itemIndex - 1], //If you are, then go to the last possible index
                    secondCondition: itemIndex == mailFavourites.length + 1 && mailFavouritesClose, //If you are not in the first index, check if you are at the index prior to the favourites folder and if it is closed
                    secondConditionMet: itemRefs[0], //If the prior condition is true, go to the rootNode of the favourites folder
                    secondConditionUnMet: itemRefs[itemIndex - 1], //Else, just go one index prior
                };
            }
            case arrowLocation.MailFooter: {
                return {
                    condition:
                        itemIndex == mailFavourites.length + 2 + mailFolders.length &&
                        mailFoldersClose, //Check if you are in the first index in the mail footer, and if the Folder rootNode in the mail module is closed
                    conditionMet: itemRefs[mailFavourites.length + 1], // If the prior condition is true, go to the exact folder rootNode and ignore inbetween nodes
                    conditionUnMet: itemRefs[itemIndex - 1], //Else just go up by one index
                };
            }
            case arrowLocation.PeopleFooter:
                return {
                    condition:
                        itemIndex == peopleFilter.length + peopleContacts.nodeIds.length + 2 &&
                        folder2ExpandedState == false, //Check if you are in the first Index of the people module footer. (The +2 is to account for the rootNodes) and check if the folder just above the footer is closed
                    conditionMet: itemRefs[itemIndex - peopleFilter.length - 1], //If the prior condition holds, jump to the rootNode of the prior folder
                    conditionUnMet: itemRefs[itemIndex - 1], // Go one index up
                };
            case arrowLocation.Default:
            default: {
                return {
                    condition: true, //No special condition to check in this case, just go one index up regardless of which index you are in
                    conditionMet: itemRefs[itemIndex - 1],
                    conditionUnMet: itemRefs[itemIndex - 1],
                };
            }
        }
    } else {
        switch (configDown) {
            case arrowLocation.MailBody: {
                return {
                    condition: itemIndex != length - 1, //Check if you are not in the end of the list
                    secondCondition: itemIndex == mailFavourites.length + 1 && mailFoldersClose, //Check if you are at the rootNode of the Folders folder and if the Folders folder is closed
                    secondConditionMet: itemRefs[itemIndex + mailFolders.length + 1], //If this is the case, go to the index after the end of the Folders folder (start of the Footer)
                    thirdCondition: itemIndex == 0 && mailFavouritesClose, // If the prior condition is false, check if you at the rootNode of the Favourites folder and if it is closed
                    thirdConditionMet: itemRefs[mailFavourites.length + 1], //If the thirdCondition holds, go to the rootNode of the Folders folder (ie, skip the favourites folder)
                    thirdConditionUnMet: itemRefs[itemIndex + 1], //Else, just go one index down
                    conditionUnMet: itemRefs[0], //If you are at the end of the list, loop back to the top
                };
            }
            case arrowLocation.PeopleBody: {
                return {
                    condition: itemIndex != length - 1, //Check if you are not in the end of the list
                    secondCondition:
                        rootNode && !folder1ExpandedState && rootDisplayName == 'Contacts', //Check if you are in the root of the contacts folder and if it is not expanded
                    secondConditionMet: itemRefs[itemIndex + peopleContacts.nodeIds.length + 1], //If the prior condition holds, go to the Folders folder rootNode (skip the contacts folder)
                    thirdCondition:
                        rootNode && !folder2ExpandedState && rootDisplayName == 'Folders', //Check if you are in the root of the folders folder and if it is not expanded
                    thirdConditionMet: itemRefs[itemIndex + peopleFilter.length + 1], //If the prior condition holds, go to the People module footer (skip the folders folder)
                    thirdConditionUnMet: itemRefs[itemIndex + 1], //If no special check is met, just go down by one index
                    conditionUnMet: itemRefs[0], //If you are at the end of the list, loop back to the top
                };
            }
            case arrowLocation.Default:
            default: {
                return {
                    condition: itemIndex == length - 1, //No extra conditions in this case. Either go one index lower, or if you at the end of the list, loop back to the top.
                    conditionMet: itemRefs[0],
                    conditionUnMet: itemRefs[itemIndex + 1],
                };
            }
        }
    }
}
